// src/features/hospitalSetting/hospitalSettingSlice.ts

import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { HospitalSetting } from "@/types/hospitalSetting"
import { fetchHospitalSettings } from "@core/api/hospitalSettingApi"

/**
 * HospitalSetting Redux State
 *
 * 다이나믹 설정 시스템:
 * - settings: 모든 HospitalSetting을 name을 키로 하는 맵으로 저장
 * - 새로운 설정이 추가되어도 타입 변경 불필요
 * - 설정 접근: getSetting(state, 'setting_name', defaultValue)
 */

export interface HospitalSettingState {
  settings: Record<string, HospitalSetting>
  loaded: boolean
  loading: boolean
  error: string | null
  fetchRequestId?: string | null
  settingRevisions?: Record<string, number>
  fetchRevisions?: Record<string, number>
  resetGeneration?: number
  fetchGeneration?: number
}

const initialState: HospitalSettingState = {
  settings: {},
  loaded: false,
  loading: false,
  error: null,
  fetchRequestId: null,
  settingRevisions: {},
  fetchRevisions: {},
  resetGeneration: 0,
  fetchGeneration: 0,
}

const hospitalSettingSlice = createSlice({
  name: "hospitalSetting",
  initialState,
  reducers: {
    // 개별 설정 업데이트
    updateSetting: (state, action: PayloadAction<HospitalSetting>) => {
      state.settings[action.payload.hospitalSettingName] = action.payload
      const revisions = (state.settingRevisions ??= {})
      const name = action.payload.hospitalSettingName
      revisions[name] = (revisions[name] ?? 0) + 1
    },

    // 개별 설정 삭제
    deleteSetting: (state, action: PayloadAction<string>) => {
      delete state.settings[action.payload]
      const revisions = (state.settingRevisions ??= {})
      revisions[action.payload] = (revisions[action.payload] ?? 0) + 1
    },

    // 상태 초기화
    resetSettings: state => ({ ...initialState, resetGeneration: (state.resetGeneration ?? 0) + 1 }),
  },
  extraReducers: builder => {
    builder
      .addCase(fetchHospitalSettings.pending, (state, action) => {
        state.loading = true
        state.error = null
        state.fetchRequestId = action.meta.requestId
        state.fetchRevisions = { ...state.settingRevisions }
      })
      .addCase(fetchHospitalSettings.fulfilled, (state, action) => {
        if (state.fetchRequestId !== action.meta.requestId) {
          return
        }
        state.loading = false
        state.loaded = true
        state.error = null
        state.fetchGeneration = (state.fetchGeneration ?? 0) + 1
        const incomingSettings = action.payload.reduce(
          (acc, setting) => {
            acc[setting.hospitalSettingName] = setting
            return acc
          },
          {} as Record<string, HospitalSetting>
        )
        const revisions = (state.settingRevisions ??= {})
        const nextSettings = { ...state.settings }
        const names = new Set([...Object.keys(nextSettings), ...Object.keys(incomingSettings)])
        for (const name of names) {
          if ((revisions[name] ?? 0) !== (state.fetchRevisions?.[name] ?? 0)) {
            continue
          }
          if (incomingSettings[name]) {
            nextSettings[name] = incomingSettings[name]
          } else {
            delete nextSettings[name]
          }
          revisions[name] = (revisions[name] ?? 0) + 1
        }
        state.settings = nextSettings
        state.fetchRequestId = null
        state.fetchRevisions = {}
      })
      .addCase(fetchHospitalSettings.rejected, (state, action) => {
        if (state.fetchRequestId !== action.meta.requestId) {
          return
        }
        state.loading = false
        state.error = (action.payload as string) || action.error.message || "설정 로드 실패"
        state.fetchRequestId = null
        state.fetchRevisions = {}
      })
  },
})

export const { updateSetting, deleteSetting, resetSettings } = hospitalSettingSlice.actions

export default hospitalSettingSlice.reducer
